import { useEffect, useReducer, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    getErrorMessage
} from '../helpers/errors';
import { setBobLoading } from '../actions/bobActions';
import { fetchRuleKPIs, fetchRules } from '../actions/ruleActions';
import useIsMounted from './useIsMounted';

const DEFAULT_RESPONSE = Object.freeze({
    error:   null,
    loading: false,
    meta:    null
});

const initialState = {
    fetching: false
};

/**
 * Using a local reducer allows the hook to track the progress of the fetch
 * rules request across multiple components without adding additional overhead
 * to the main Redux store.
 */
function reducer(state, action) {
    switch (action) {
        case 'FETCH_BEGIN':
            return {
                ...initialState,
                fetching: true
            };
        case 'FETCH_END':
            return {
                ...initialState,
                fetching: false
            };
        default:
            return initialState;
    }
}


export default function useRules() {
    const [state, localDispatch] = useReducer(reducer, initialState);
    const isMounted = useIsMounted();
    const dispatch = useDispatch();
    const rules = useSelector(
        ({ rules }) => Object.values(rules.items)
    );
    const [response, setResponse] = useState({ ...DEFAULT_RESPONSE });
    const [params, setParams] = useState();

    useEffect(() => {
        if (state.fetching || !params) {
            return;
        }
        localDispatch('FETCH_BEGIN');
        async function runFetch() {
            setResponse({
                ...DEFAULT_RESPONSE,
                error:   null,
                loading: true
            });
            try {
                dispatch(setBobLoading('RuleSearching'));

                const promises = [dispatch(fetchRules(params))];
                if (process.env.ruleBuilderUpdatedMetricsEnabled) {
                    promises.push(dispatch(fetchRuleKPIs()));
                }
                await Promise.all(promises).then(results => {
                    let meta = results[0]?.meta;
                    results.forEach(result => {
                        if (result.data) {
                            setResponse({
                                ...DEFAULT_RESPONSE,
                                complete: true,
                                loading:  false,
                                meta:     result.meta
                            });
                        }
                        else if (result.totalCount) {
                            if (isMounted.current) {
                                setResponse({
                                    ...DEFAULT_RESPONSE,
                                    complete: true,
                                    kpis:     result,
                                    loading:  false,
                                    meta:     meta
                                });
                            }
                        }
                    });
                });
            } catch (error) {
                setResponse({
                    ...DEFAULT_RESPONSE,
                    error: getErrorMessage(error)
                });
            } finally {
                localDispatch('FETCH_END');
                dispatch(setBobLoading('RuleSearching'));
            }
        }
        runFetch();
    }, [params]);

    return [
        {
            ...response,
            data: state.fetching ? [] : rules
        },
        (params) => setParams({ ...params })
    ];
}
